import type { ComponentType } from 'preact';
import { useEffect } from 'preact/hooks';
import { ErrorBoundary, LocationProvider, Route, Router, useLocation } from 'preact-iso';

import type { ModuleIdValue } from '@amumin/api-client';

import { EmptyState } from './lib/EmptyState.js';
import { CrashBoundary } from './components/CrashBoundary.js';
import { lazyPage } from './components/LazyPage.js';
import { Protected } from './components/Protected.js';
import { LoginPage } from './pages/Login.js';
import { LoginCodePage } from './pages/LoginCode.js';
import { NotFoundPage } from './pages/NotFound.js';
import { NotMePage } from './pages/NotMe.js';
import { RegisterPage } from './pages/Register.js';
import { ResetPage } from './pages/Reset.js';
import { ResetConfirmPage } from './pages/ResetConfirm.js';
import { TodayPage } from './pages/Today.js';
import { VerifyPage } from './pages/Verify.js';
import { redirectTo } from './pages/settingsParts.js';
import { hubbed } from './lib/hub.js';
import { sheetRoute } from './lib/routeSheet.js';
import { hubTabs, type HubId } from './lib/navigation.js';
import { writePref } from './lib/ui.js';
import { moduleOn, moduleUnlisted, modulesReady } from './state/modules.js';
import { appText, authState, context, messages } from './state/session.js';

// Routes van de ouderapp (PLAN S1-09, sprint 2/3; ONTWERP.md §11/§11.1; DESIGN.md §8.W1–W12).
// Schermen zonder sessie staan los; de rest zit achter <Protected> (fb_sess) in de Shell.

// Ontwerpgalerij (docs/DESIGN.md §9): alleen in development, lazy geladen en nooit in de
// productienavigatie; in een productiebuild valt de hele module weg (import.meta.env.DEV = false).
const DesignGallery = import.meta.env.DEV
  ? lazyPage(() => import('./pages/DesignGallery.js').then((mod) => mod.DesignGalleryPage))
  : null;

// Minder vaak gebruikte schermen laden pas als je ze opent (kleinere eerste lading op de telefoon).
const AgendaPage = lazyPage(() => import('./pages/Agenda.js').then((mod) => mod.AgendaPage));
const EtenPage = lazyPage(() => import('./pages/eten/EtenPage.js').then((mod) => mod.EtenPage));
// w4 familie: wensen, berichten, kalenders, familie; /w is de deelpagina zonder account of schil.
const CalendarsPage = lazyPage(() =>
  import('./family/CalendarsPage.js').then((mod) => mod.CalendarsPage),
);
const GuestPage = lazyPage(() => import('./family/GuestPage.js').then((mod) => mod.GuestPage));
const LinkAcceptPage = lazyPage(() =>
  import('./family/LinksPage.js').then((mod) => mod.LinkAcceptPage),
);
const LinksPage = lazyPage(() => import('./family/LinksPage.js').then((mod) => mod.LinksPage));
const NotesPage = lazyPage(() => import('./notes/NotesPage.js').then((mod) => mod.NotesPage));
// 0068: Gezond op gewicht (eigen chunk; persoonlijk, alleen volwassenen).
const KompasPage = lazyPage(() => import('./kompas/KompasPage.js').then((mod) => mod.KompasPage));
const MoneyPage = lazyPage(() => import('./money/MoneyPage.js').then((mod) => mod.MoneyPage));
// 0074: terug van de bank (bankkoppeling van Geldzaken).
const BankReturnPage = lazyPage(() =>
  import('./money/BankReturn.js').then((mod) => mod.BankReturnPage),
);
const PhotoFramePage = lazyPage(() =>
  import('./photoFrame/PhotoFramePage.js').then((mod) => mod.PhotoFramePage),
);
// 0061: Kerk & kalender (onder Wat past bij ons gezin).
const SeasonsPage = lazyPage(() =>
  import('./seasons/SeasonsPage.js').then((mod) => mod.SeasonsPage),
);
const MessagesPage = lazyPage(() =>
  import('./family/MessagesPage.js').then((mod) => mod.MessagesPage),
);
const SharePage = lazyPage(() => import('./family/SharePage.js').then((mod) => mod.SharePage));
const WishesPage = lazyPage(() => import('./family/WishesPage.js').then((mod) => mod.WishesPage));
// Sprint 4 kamers: kamers met bewoners en kindslot (Schoonmaak staat in Taken, pages/Tasks.tsx).
const KidLockPage = lazyPage(() =>
  import('./kamers/KidLockPage.js').then((mod) => mod.KidLockPage),
);
const RoomDetailPage = lazyPage(() =>
  import('./kamers/RoomDetailPage.js').then((mod) => mod.RoomDetailPage),
);
const RoomsPage = lazyPage(() => import('./kamers/RoomsPage.js').then((mod) => mod.RoomsPage));
const DevicesPage = lazyPage(() => import('./pages/Devices.js').then((mod) => mod.DevicesPage));
// Scherm inrichten (team 2): widgets van het bord per tablet.
const BoardConfigPage = lazyPage(() =>
  import('./board-config/BoardConfigPage.js').then((mod) => mod.BoardConfigPage),
);
// 0028: Virtuele iPad (module virtual_device): het echte bord in een iPad-lijst, alleen ouders.
const VirtualIpadPage = lazyPage(() =>
  import('./virtual/VirtualIpadPage.js').then((mod) => mod.VirtualIpadPage),
);
// Menu Board (oprichter 1 okt 2026): het echte bord van een tablet, alleen kijken.
const BoardViewPage = lazyPage(() =>
  import('./board/BoardViewPage.js').then((mod) => mod.BoardViewPage),
);
const MembersPage = lazyPage(() => import('./pages/Members.js').then((mod) => mod.MembersPage));
const PairPage = lazyPage(() => import('./pages/Pair.js').then((mod) => mod.PairPage));
const SecurityPage = lazyPage(() => import('./pages/Security.js').then((mod) => mod.SecurityPage));
// 0027: extra modules van het gezinsprofiel (bestaat alleen als het profiel ze toelaat; API 404).
const ExtrasPage = lazyPage(() => import('./pages/Extras.js').then((mod) => mod.ExtrasPage));
const ExtrasSourcesPage = lazyPage(() =>
  import('./pages/Extras.js').then((mod) => mod.ExtrasSourcesPage),
);
// 0063: module Hindoeïsme (bestaat alleen met hindoeïsme in het profiel; API 404).
const AlmanacPage = lazyPage(() =>
  import('./almanac/AlmanacPage.js').then((mod) => mod.AlmanacPage),
);
const SettingsPage = lazyPage(() => import('./pages/Settings.js').then((mod) => mod.SettingsPage));
// 0029: modules per gezin (Instellingen · Modules) en het rustige scherm als een module uit staat.
const ModulesPage = lazyPage(() => import('./pages/Modules.js').then((mod) => mod.ModulesPage));
// Oprichter 1 okt: samengevoegde instellingen (Huishouden = naam, taal en soort; Agenda koppelen en
// exporteren = abonnementslinks, reistijd en .ics; Locatie en weer).
const HouseholdSettingsPage = lazyPage(() =>
  import('./pages/SettingsGroups.js').then((mod) => mod.HouseholdSettingsPage),
);
const CalendarSettingsPage = lazyPage(() =>
  import('./pages/SettingsGroups.js').then((mod) => mod.CalendarSettingsPage),
);
// WP3: Account en privacy (beveiliging, meldingen, thema, abonnement, gegevens).
const AccountSettingsPage = lazyPage(() =>
  import('./pages/SettingsGroups.js').then((mod) => mod.AccountSettingsPage),
);
const LocationWeatherPage = lazyPage(() =>
  import('./pages/SettingsGroups.js').then((mod) => mod.LocationWeatherPage),
);
const ModuleOffPage = lazyPage(() => import('./pages/Modules.js').then((mod) => mod.ModuleOffPage));
// 0038: meldingen op je telefoon (Instellingen · Meldingen, beginschermapp).
const NotificationsPage = lazyPage(() =>
  import('./pages/Notifications.js').then((mod) => mod.NotificationsPage),
);
// 0023: Wat past bij ons gezin (alleen ouders).
// 0062: module Jodendom (Sjabbat en joodse kalender).
const JudaismSettingsPage = lazyPage(() =>
  import('./jodendom/JudaismSettingsPage.js').then((mod) => mod.JudaismSettingsPage),
);
const ProfileSettingsPage = lazyPage(() =>
  import('./profile/ProfileSettingsPage.js').then((mod) => mod.ProfileSettingsPage),
);
// 0032 (golf 3): Groei (module Gezondheid) en School; de pagina van één lid met tabs.
const GrowthPage = lazyPage(() => import('./groei/GrowthPage.js').then((mod) => mod.GrowthPage));
const SchoolPage = lazyPage(() => import('./school/SchoolPage.js').then((mod) => mod.SchoolPage));
// 0048 (golf 7): Soera's leren (Leerdoelen; alleen bij Islam en Koranles, de API geeft anders 404).
const LearningPage = lazyPage(() =>
  import('./memorize/LearningPages.js').then((mod) => mod.LearningPage),
);
const LearningMemberPage = lazyPage(() =>
  import('./memorize/LearningPages.js').then((mod) => mod.LearningMemberPage),
);
const LearningListPage = lazyPage(() =>
  import('./memorize/LearningPages.js').then((mod) => mod.LearningListPage),
);
const LearningSettingsPage = lazyPage(() =>
  import('./memorize/LearningPages.js').then((mod) => mod.LearningSettingsPage),
);
const LearningSetupPage = lazyPage(() =>
  import('./memorize/LearningPages.js').then((mod) => mod.LearningSetupPage),
);
// 0049 (golf 8): Samen wonen (Wie eet mee, Kosten delen, Reserveren).
const DinnerPage = lazyPage(() => import('./samen/DinnerPage.js').then((mod) => mod.DinnerPage));
const CostsPage = lazyPage(() => import('./samen/CostsPage.js').then((mod) => mod.CostsPage));
const BookingsPage = lazyPage(() =>
  import('./samen/BookingsPage.js').then((mod) => mod.BookingsPage),
);
// Golf 8 (0050): Check-in en kring (beheerders), Mijn kring (meekijker) en de uitnodiging.
const CarePage = lazyPage(() => import('./care/CarePage.js').then((mod) => mod.CarePage));
const MyCirclePage = lazyPage(() => import('./care/CarePage.js').then((mod) => mod.MyCirclePage));
const CircleInvitePage = lazyPage(() =>
  import('./care/CircleInvitePage.js').then((mod) => mod.CircleInvitePage),
);
// Golf 8 ronde 1 (ON-3): de uitnodiging voor een huisgenoot (/uitnodiging/huis#token=…).
const HouseInvitePage = lazyPage(() =>
  import('./samen/HouseInvitePage.js').then((mod) => mod.HouseInvitePage),
);
const MemberDetailPage = lazyPage(() =>
  import('./pages/MemberDetail.js').then((mod) => mod.MemberDetailPage),
);
const TasksPage = lazyPage(() => import('./pages/Tasks.js').then((mod) => mod.TasksPage));
// Oprichter 6 okt: Mijn to-do's (Mijn omgeving) en de weg erheen vanuit het gezin.
const MyTodosPage = lazyPage(() => import('./pages/MyTodos.js').then((mod) => mod.MyTodosPage));
const WizardPage = lazyPage(() => import('./pages/Wizard.js').then((mod) => mod.WizardPage));
// 0065: aanmelden (stap 1-3 zonder sessie), voorwaarden (concept) en Instellingen › Abonnement.
const SignupPage = lazyPage(() => import('./signup/SignupPage.js').then((mod) => mod.SignupPage));
const TermsPage = lazyPage(() => import('./signup/TermsPage.js').then((mod) => mod.TermsPage));
const BillingPage = lazyPage(() =>
  import('./billing/BillingPage.js').then((mod) => mod.BillingPage),
);

// Golf 5: de volledige privacyverklaring (concept); de tekst laadt pas op /privacy.
const PrivacyPage = lazyPage(() => import('./pages/Privacy.js').then((mod) => mod.PrivacyPage));
// 0036: privacyrechten (Instellingen › Jouw gegevens; DESIGN.md §8.PR).
const PrivacyRightsPage = lazyPage(() =>
  import('./privacy/PrivacyRightsPage.js').then((mod) => mod.PrivacyRightsPage),
);

// Platformbeheerder: Werklijst (alleen met platform_admin in de auth-context; de API geeft anders 404).
const WerklijstPage = lazyPage(() =>
  import('./platform/WerklijstPage.js').then((mod) => mod.WerklijstPage),
);
// AMUMIN Beheer (golf 4, DESIGN.md §8.BH): klantenconsole; voor ieder ander NotFound (API 404).
const BeheerHouseholdsPage = lazyPage(() =>
  import('./platform/BeheerPage.js').then((mod) => mod.BeheerHouseholdsPage),
);
const BeheerCreatePage = lazyPage(() =>
  import('./platform/BeheerPage.js').then((mod) => mod.BeheerCreatePage),
);
const BeheerHouseholdPage = lazyPage(() =>
  import('./platform/BeheerPage.js').then((mod) => mod.BeheerHouseholdPage),
);
const BeheerAccountsPage = lazyPage(() =>
  import('./platform/BeheerPage.js').then((mod) => mod.BeheerAccountsPage),
);
const BeheerAuditPage = lazyPage(() =>
  import('./platform/BeheerPage.js').then((mod) => mod.BeheerAuditPage),
);
// Uitnodiging van een eigenaar die Beheer aanmaakte (openbaar, token in het fragment).
const OwnerInvitePage = lazyPage(() =>
  import('./platform/OwnerInvitePage.js').then((mod) => mod.OwnerInvitePage),
);

/** Golf 8: de rol van deze login in het actieve huishouden. */
function activeRole(): string | undefined {
  const ctx = context.value;
  return ctx?.households.find((x) => x.id === ctx.active_household_id)?.role;
}

function guard(
  Page: ComponentType,
  opts: { bare?: boolean; module?: ModuleIdValue; relative?: boolean } = {},
) {
  return function GuardedPage() {
    // Golf 8 (0050; §3.3.4): een meekijker komt alleen op Vandaag, Agenda, Mijn kring, Meldingen
    // en Account; een directe URL naar iets anders geeft een rustige "niet voor jou" (de API
    // weigert het ook met 403).
    if (activeRole() === 'relative' && !opts.relative) {
      return (
        <Protected bare={opts.bare}>
          <EmptyState
            title={messages.value.web.errorNotFound}
            body={appText.value.care.notForRelative}
            name="relative-forbidden"
          />
        </Protected>
      );
    }
    // 0029: staat de module van dit scherm uit, dan een rustige uitleg in plaats van het scherm.
    // Eerst wachten tot de schil de modules kent (anders flitst het scherm met een 404-melding).
    const mod = opts.module;
    const waiting = mod !== undefined && !modulesReady.value;
    // Soera's leren bestaat alleen bij Islam en Koranles: noemt de server hem niet, dan is het een
    // onbekende pagina, zonder woord over de module (DESIGN §8.GB.1; review g7 r1). Alleen 'uit'
    // als hij in de lijst staat en uit is.
    const absent = mod !== undefined && !waiting && moduleUnlisted(mod);
    const off = mod !== undefined && !waiting && !moduleOn(mod);
    return (
      <Protected bare={opts.bare}>
        {waiting ? (
          <p class="page-loading">{messages.value.web.loading}</p>
        ) : absent ? (
          <EmptyState title={messages.value.web.errorNotFound} name="module-absent" />
        ) : off && mod ? (
          <ModuleOffPage id={mod} />
        ) : (
          <Page />
        )}
      </Protected>
    );
  };
}

/**
 * Alleen voor de platformbeheerder. Wie ingelogd is maar geen beheerder, krijgt de gewone
 * NotFound-pagina (de pagina bestaat voor hem niet); de module wordt dan ook nooit geladen.
 */
function adminOnly(Page: ComponentType) {
  return function AdminPage() {
    if (authState.value === 'authenticated' && context.value?.platform_admin !== true) {
      return <NotFoundPage />;
    }
    return (
      <Protected>
        <Page />
      </Protected>
    );
  };
}

/**
 * Golf 8: /kring is Mijn kring (meekijker), en met #token=… de landingspagina van de uitnodiging
 * uit de e-mail (ook zonder sessie).
 */
const GuardedMyCircle = guard(MyCirclePage, { relative: true });
function CircleRoute() {
  const invite = typeof window !== 'undefined' && /(?:^#|&)token=/.test(window.location.hash);
  return invite ? <CircleInvitePage /> : <GuardedMyCircle />;
}

/** / gaat naar Vandaag. */
const Home = redirectTo('/vandaag');

/**
 * WP2: het adres van een plek onder Meer (/prikbord, /kinderen, /samen, /bord, /gezin) gaat naar het
 * eerste tabblad dat hier zichtbaar is (modules en rol); is er geen, dan is de pagina er niet. De
 * tabbladen zelf houden hun bestaande adres (lib/navigation.ts HUBS).
 */
function hubIndex(id: HubId): ComponentType {
  return function HubIndex() {
    const { route } = useLocation();
    const first = modulesReady.value ? hubTabs(id)[0]?.href : undefined;
    useEffect(() => {
      if (first) route(first, true);
    }, [first, route]);
    if (modulesReady.value && !first) return <NotFoundPage />;
    return <p class="page-loading">{messages.value.web.loading}</p>;
  };
}

/**
 * WP2: een oud adres opent een weergave binnen een ander scherm. Taken en Agenda onthouden hun
 * weergave in deze browser (lib/ui.tsx readPref, sleutels in pages/Tasks.tsx en pages/Agenda.tsx);
 * we zetten die keuze en gaan erheen, met de zoekvraag en het #-stuk erbij. Vervangt de regel in
 * de geschiedenis, dus Terug gaat niet heen en weer.
 */
const VIEW_REDIRECTS = {
  '/ritten': { to: '/agenda', key: 'fb.agenda.mode', value: 'rides' },
  '/beloningen': { to: '/taken', key: 'fb.tasks.view', value: 'stars' },
  '/goedkeuren': { to: '/taken', key: 'fb.tasks.view', value: 'stars' },
  // Afronding "één geheel": Schoonmaak en Rooster zijn het filter Klusjes onder Taken > Alle.
  '/schoonmaak': { to: '/taken', key: 'fb.tasks.view', value: 'chores' },
  '/rooster': { to: '/taken', key: 'fb.tasks.view', value: 'chores' },
  // Oprichter 6 okt: de kaart To-do's op Vandaag opent Taken › To-do.
  '/to-do': { to: '/taken', key: 'fb.tasks.view', value: 'todo' },
} as const;

function openView(target: { to: string; key: string; value: string }): ComponentType {
  return function OpenView() {
    const { route } = useLocation();
    useEffect(() => {
      writePref(target.key, target.value);
      route(`${target.to}${window.location.search}${window.location.hash}`, true);
    }, [route]);
    return <p class="page-loading">{messages.value.web.loading}</p>;
  };
}

const RIDES_VIEW = openView(VIEW_REDIRECTS['/ritten']);
const STARS_VIEW = openView(VIEW_REDIRECTS['/goedkeuren']);

/**
 * Afronding "één geheel" (compact-plan hoofdstuk 3, tabel in ontwerpsysteem.md): detailpagina's
 * als blad met een eigen adres boven hun ouder (lib/routeSheet.tsx). Het adres blijft werken
 * (e-mail, push, QR, Terug van de browser); sluiten gaat naar de ouder. Koppelen (/pair) en Recept
 * (EtenPage) doen dat zelf.
 */
const toModules = () => '/instellingen/modules';
const MEMBER_SHEET = sheetRoute(MembersPage, MemberDetailPage, {
  name: 'member-sheet',
  parent: () => '/leden',
});
const ROOM_SHEET = sheetRoute(RoomsPage, RoomDetailPage, { name: 'room-sheet' });
const LINK_SHEET = sheetRoute(LinksPage, LinkAcceptPage, { name: 'link-accept-sheet' });
const SOURCES_SHEET = sheetRoute(ExtrasPage, ExtrasSourcesPage, { name: 'prayer-sources-sheet' });
const LEARNING_SETUP_SHEET = sheetRoute(LearningPage, LearningSetupPage, {
  name: 'learning-setup-sheet',
});
const CHURCH_SHEET = sheetRoute(ProfileSettingsPage, SeasonsPage, { name: 'church-sheet' });
const JUDAISM_SHEET = sheetRoute(ModulesPage, JudaismSettingsPage, {
  name: 'judaism-sheet',
  parent: toModules,
});
const ALMANAC_SHEET = sheetRoute(ModulesPage, AlmanacPage, {
  name: 'almanac-sheet',
  parent: toModules,
});
const CHORES_VIEW = openView(VIEW_REDIRECTS['/schoonmaak']);
const TODO_VIEW = openView(VIEW_REDIRECTS['/to-do']);

export function App() {
  return (
    <LocationProvider>
      <CrashBoundary>
        <ErrorBoundary>
          <Router>
            <Route path="/" component={Home} />
            <Route path="/login" component={LoginPage} />
            <Route path="/login/code" component={LoginCodePage} />
            <Route path="/register" component={RegisterPage} />
            <Route path="/verify" component={VerifyPage} />
            <Route path="/aanmelden" component={SignupPage} />
            <Route path="/voorwaarden" component={TermsPage} />
            <Route path="/reset" component={ResetPage} />
            <Route path="/reset/confirm" component={ResetConfirmPage} />
            <Route path="/not-me" component={NotMePage} />
            {/* 0023: privacyverklaring, openbaar (link in het toestemmingsblok). */}
            <Route path="/privacy" component={PrivacyPage} />
            <Route path="/wizard" component={guard(WizardPage, { bare: true })} />
            <Route path="/vandaag" component={guard(TodayPage, { relative: true })} />
            {/* Menu Board: het echte bord van een tablet live bekijken (alleen ouders). */}
            <Route path="/board" component={guard(BoardViewPage)} />
            <Route path="/agenda" component={guard(AgendaPage, { relative: true })} />
            {/* Golf 8 (0050): Mijn kring en de uitnodiging (#token=…). */}
            <Route path="/kring" component={CircleRoute} />
            <Route path="/instellingen/zorg" component={guard(CarePage)} />
            {/* WP2: Ritten is een weergave van Agenda, Beloningen en Goedkeuren het tabblad Sterren
                van Taken (WP4); de oude adressen openen die weergave. */}
            <Route path="/ritten" component={guard(RIDES_VIEW, { module: 'rides' })} />
            <Route path="/taken" component={guard(TasksPage)} />
            <Route path="/to-do" component={guard(TODO_VIEW, { module: 'todos' })} />
            <Route path="/mijn-todo" component={guard(MyTodosPage)} />
            <Route path="/beloningen" component={guard(STARS_VIEW)} />
            {/* Golf 6b (0043): beurtrooster (huisgenoten); nu Taken > Alle > Klusjes. */}
            <Route path="/rooster" component={guard(CHORES_VIEW, { module: 'chore_rotation' })} />
            {/* Golf 8 (0049): Samen wonen. */}
            <Route
              path="/wie-eet-mee"
              component={guard(hubbed('samen', DinnerPage), { module: 'dinner_list' })}
            />
            <Route
              path="/kosten"
              component={guard(hubbed('samen', CostsPage), { module: 'expenses' })}
            />
            <Route
              path="/reserveren"
              component={guard(hubbed('samen', BookingsPage), { module: 'bookings' })}
            />
            <Route path="/goedkeuren" component={guard(STARS_VIEW)} />
            {/* Sprint 4 huishouden: boodschappen, recepten, weekmenu, voorraad (DESIGN.md §8.H). */}
            <Route path="/eten" component={guard(EtenPage, { module: 'food' })} />
            <Route path="/eten/:tab" component={guard(EtenPage, { module: 'food' })} />
            <Route path="/eten/recepten/:id" component={guard(EtenPage, { module: 'food' })} />
            {/* w4 familie (DESIGN.md §8.F). */}
            <Route path="/wensen" component={guard(WishesPage, { module: 'wishes' })} />
            <Route path="/berichten" component={guard(MessagesPage, { module: 'messages' })} />
            {/* 0060: notities (module Notities). */}
            <Route path="/notities" component={guard(NotesPage, { module: 'notes' })} />
            {/* 0064: Fotolijst (module Fotolijst, alleen ouders). */}
            <Route path="/fotolijst" component={guard(PhotoFramePage, { module: 'photo_frame' })} />
            {/* 0068: Gezond op gewicht (de pagina zegt zelf rustig als de module uit staat). */}
            <Route path="/kompas" component={guard(KompasPage)} />
            {/* 0069: Geldzaken (privé, in je eigen omgeving). */}
            <Route path="/geldzaken" component={guard(MoneyPage, { module: 'money' })} />
            <Route path="/geldzaken/bank" component={guard(BankReturnPage, { module: 'money' })} />
            <Route path="/kalenders" component={guard(CalendarsPage)} />
            <Route path="/familie" component={guard(LinksPage)} />
            <Route path="/familie/koppelen" component={guard(LINK_SHEET)} />
            <Route path="/familie/:id" component={guard(GuestPage)} />
            <Route path="/w" component={SharePage} />
            {/* Sprint 4 kamers: kamers, schoonmaak, kindslot (DESIGN.md §8.R). */}
            {/* Schoonmaak: Taken > Alle > Klusjes (het oude adres opent die weergave). */}
            <Route path="/schoonmaak" component={guard(CHORES_VIEW, { module: 'cleaning' })} />
            <Route path="/kamers" component={guard(RoomsPage, { module: 'rooms' })} />
            <Route path="/kamers/:id" component={guard(ROOM_SHEET, { module: 'rooms' })} />
            <Route path="/kindslot" component={guard(KidLockPage)} />
            <Route path="/leden" component={guard(MembersPage)} />
            <Route path="/leden/:id/:tab?" component={guard(MEMBER_SHEET)} />
            <Route path="/groei" component={guard(GrowthPage, { module: 'health' })} />
            <Route path="/school" component={guard(SchoolPage, { module: 'school' })} />
            <Route path="/leerdoelen" component={guard(LearningPage, { module: 'memorize' })} />
            <Route
              path="/leerdoelen/lijst"
              component={guard(LearningListPage, { module: 'memorize' })}
            />
            <Route
              path="/leerdoelen/instellingen"
              component={guard(LearningSettingsPage, { module: 'memorize' })}
            />
            <Route
              path="/leerdoelen/instellen"
              component={guard(LEARNING_SETUP_SHEET, { module: 'memorize' })}
            />
            <Route
              path="/leerdoelen/:memberId"
              component={guard(LearningMemberPage, { module: 'memorize' })}
            />
            <Route path="/apparaten" component={guard(DevicesPage)} />
            <Route path="/apparaten/:id/scherm" component={guard(BoardConfigPage)} />
            <Route
              path="/apparaten/virtueel"
              component={guard(VirtualIpadPage, { module: 'virtual_device' })}
            />
            {/* WP2: de plekken onder Meer als eigen adres; ze openen het eerste zichtbare tabblad. */}
            <Route path="/prikbord" component={guard(hubIndex('prikbord'))} />
            <Route path="/kinderen" component={guard(hubIndex('kinderen'))} />
            <Route path="/samen" component={guard(hubIndex('samen'))} />
            <Route path="/bord" component={guard(hubIndex('bord'))} />
            <Route path="/gezin" component={guard(hubIndex('gezin'))} />
            <Route path="/pair" component={guard(PairPage)} />
            <Route path="/instellingen" component={guard(SettingsPage)} />
            <Route
              path="/instellingen/beveiliging"
              component={guard(SecurityPage, { relative: true })}
            />
            <Route path="/instellingen/modules" component={guard(ModulesPage)} />
            <Route path="/instellingen/gezin" component={guard(HouseholdSettingsPage)} />
            <Route path="/instellingen/agenda" component={guard(CalendarSettingsPage)} />
            <Route path="/instellingen/account" component={guard(AccountSettingsPage)} />
            {/* Oude adressen (voor 1 okt) sturen door naar het blok op de groepspagina. */}
            <Route path="/instellingen/soort" component={redirectTo('/instellingen/gezin#soort')} />
            <Route path="/instellingen/taal" component={redirectTo('/instellingen/gezin#taal')} />
            <Route path="/instellingen/weer" component={redirectTo('/instellingen/locatie#weer')} />
            <Route
              path="/instellingen/reistijd"
              component={redirectTo('/instellingen/agenda#reistijd')}
            />
            <Route
              path="/instellingen/agenda-koppelen"
              component={redirectTo('/instellingen/agenda#koppelen')}
            />
            <Route
              path="/instellingen/meldingen"
              component={guard(NotificationsPage, { module: 'push', relative: true })}
            />
            <Route
              path="/instellingen/locatie"
              component={guard(LocationWeatherPage, { module: 'location_weather' })}
            />
            <Route
              path="/instellingen/gezin/extra"
              component={guard(ExtrasPage, { module: 'prayer' })}
            />
            <Route
              path="/instellingen/gezin/kalender"
              component={guard(ALMANAC_SHEET, { module: 'almanac' })}
            />
            <Route
              path="/instellingen/gezin/extra/bronnen"
              component={guard(SOURCES_SHEET, { module: 'prayer' })}
            />
            <Route path="/instellingen/wat-past" component={guard(ProfileSettingsPage)} />
            <Route
              path="/instellingen/gezin/tijden"
              component={guard(JUDAISM_SHEET, { module: 'shabbat' })}
            />
            <Route path="/instellingen/wat-past/kerk" component={guard(CHURCH_SHEET)} />
            <Route path="/instellingen/gegevens" component={guard(PrivacyRightsPage)} />
            <Route path="/instellingen/abonnement" component={guard(BillingPage)} />
            {/* Beheer (platformbeheerder, PLATFORM_ADMIN_EMAILS; DESIGN.md §8.WL). */}
            <Route path="/beheer/werklijst" component={adminOnly(WerklijstPage)} />
            <Route path="/beheer" component={adminOnly(BeheerHouseholdsPage)} />
            <Route path="/beheer/nieuw" component={adminOnly(BeheerCreatePage)} />
            <Route path="/beheer/huishouden/:id" component={adminOnly(BeheerHouseholdPage)} />
            <Route path="/beheer/accounthulp" component={adminOnly(BeheerAccountsPage)} />
            <Route path="/beheer/logboek" component={adminOnly(BeheerAuditPage)} />
            <Route path="/uitnodiging" component={OwnerInvitePage} />
            <Route path="/uitnodiging/huis" component={HouseInvitePage} />
            <Route path="/_ontwerp" component={DesignGallery ?? NotFoundPage} />
            <Route default component={NotFoundPage} />
          </Router>
        </ErrorBoundary>
      </CrashBoundary>
    </LocationProvider>
  );
}
